// src/components/dialogs/StripeDialog.tsx
import { LockOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import { VITE_STRIPE_PUBLIC_KEY } from "../../config/config";
import { paymentApi } from "../../data/api/PaymentApi";

const stripePromise = loadStripe(VITE_STRIPE_PUBLIC_KEY);

interface StripeDialogProps {
  open: boolean;
  onClose: () => void;
  planCode: string;
  amountUsd: number;
}

export const StripeDialog: React.FC<StripeDialogProps> = ({
  open,
  onClose,
  planCode,
  amountUsd,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSession = async () => {
      if (!open) return;
      setLoading(true);
      setClientSecret(null);
      setError(null);
      try {
        if (!planCode) {
          throw new Error("No se encontro el plan seleccionado.");
        }
        const res = await paymentApi.getPaymentUrl.request({ planCode });
        if (res?.data?.clientSecret) {
          setClientSecret(res.data.clientSecret);
        } else {
          throw new Error(res.messageError || "No se pudo iniciar el pago.");
        }
      } catch (err: unknown) {
        console.error("Error creando sesión:", err);
        setError(
          err instanceof Error ? err.message : "No se pudo iniciar el pago.",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [open, planCode, amountUsd]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: "white",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.5,
          background:
            "linear-gradient(90deg, #0E1EFD 0%, #0066FF 60%, #0099FF 100%)",
          color: "#fff",
          fontWeight: 700,
          fontSize: "1.1rem",
          letterSpacing: 0.3,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 38,
              height: 38,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.15)",
              boxShadow: "inset 0 0 8px rgba(255,255,255,0.2)",
            }}
          >
            <LockOutlined sx={{ color: "white", fontSize: 22 }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                lineHeight: 1.1,
                mb: 0.3,
              }}
            >
              Pago seguro
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                opacity: 0.9,
                fontWeight: 400,
              }}
            >
              Protegido con Stripe & Convisas
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            bgcolor: "rgba(255,255,255,0.1)",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.2)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          bgcolor: "white",
          p: { xs: 0, sm: 4 },
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1000,
            mt: { xs: 0, sm: 4 },
            borderRadius: 3,
            bgcolor: "white",
            overflow: "hidden",
            "& iframe": {
              width: "100% !important",
              minHeight: "90vh",
              border: "none",
            },
          }}
        >
          {loading && !clientSecret && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                flexDirection: "column",
                bgcolor: "#fff",
              }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Cargando Stripe...</Typography>
            </Box>
          )}

          {error && !loading && (
            <Alert severity="error" sx={{ m: 3 }}>
              {error}
            </Alert>
          )}

          {clientSecret && (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
